/**
 * useContextSnapshot — the shared React binding for the intelligence layer.
 *
 * Strict layering: pages call an intelligence hook; the hooks call THIS hook,
 * which owns a {@link ContextRetriever} (loads the entity snapshot through the
 * feature stores) and a thin `converse` binding to the REAL Bedrock op via the
 * api facade. Pages never import a store, the retriever, or the api directly.
 *
 * It loads the snapshot once, projects it into {@link EntityRef}s, and exposes a
 * ref-by-id map so any surface can turn an id into a titled, linkable source.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { KnowledgeBaseApi, type KbCitation, type KbSource } from '../../api';
import {
  ContextRetriever,
  toEntityRefs,
} from './contextRetriever';
import {
  ANSWER_MAX_TOKENS,
  ANSWER_TEMPERATURE,
  INTELLIGENCE_MODEL_ID,
} from './intelligenceModel';
import type { EntityRef, EntitySnapshot } from './types';

export interface ConverseOptions {
  maxTokens?: number;
  temperature?: number;
}

/** Result of a KB-grounded generation via the shared snapshot hook. */
export interface KbGenerateResult {
  answer: string;
  citations: KbCitation[];
  sessionId?: string;
}

export interface UseContextSnapshotResult {
  snapshot: EntitySnapshot | null;
  refs: EntityRef[];
  refById: Map<string, EntityRef>;
  currentAlias: string;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Calls REAL Bedrock converse; resolves the output text or throws. */
  converse: (
    prompt: string,
    systemPrompt: string,
    options?: ConverseOptions,
  ) => Promise<string>;
  /** Retrieves KB chunks (Retrieve) for the configured KB; resolves sources or throws. */
  kbRetrieve: (query: string, numberOfResults?: number) => Promise<KbSource[]>;
  /** KB-grounded answer + citations (RetrieveAndGenerate) for the configured KB. */
  kbRetrieveAndGenerate: (
    query: string,
    sessionId?: string,
  ) => Promise<KbGenerateResult>;
}

export function useContextSnapshot(): UseContextSnapshotResult {
  const api = useApi();
  const retriever = useMemo(() => new ContextRetriever(api), [api]);

  const [snapshot, setSnapshot] = useState<EntitySnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await retriever.loadSnapshot();
      setSnapshot(snap);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load team context.');
    } finally {
      setLoading(false);
    }
  }, [retriever]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const refs = useMemo(
    () => (snapshot ? toEntityRefs(snapshot) : []),
    [snapshot],
  );

  const refById = useMemo(() => {
    const m = new Map<string, EntityRef>();
    for (const r of refs) m.set(r.id, r);
    return m;
  }, [refs]);

  const converse = useCallback(
    async (
      prompt: string,
      systemPrompt: string,
      options: ConverseOptions = {},
    ): Promise<string> => {
      const response = await api.inference.converse({
        modelId: INTELLIGENCE_MODEL_ID,
        prompt,
        systemPrompt,
        maxTokens: options.maxTokens ?? ANSWER_MAX_TOKENS,
        temperature: options.temperature ?? ANSWER_TEMPERATURE,
      });
      return response.outputText;
    },
    [api],
  );

  const kbRetrieve = useCallback(
    async (query: string, numberOfResults?: number): Promise<KbSource[]> => {
      const response = await api.knowledgeBase.retrieve({
        query,
        ...(numberOfResults !== undefined ? { numberOfResults } : {}),
      });
      return KnowledgeBaseApi.parseRetrieveResults(response.results);
    },
    [api],
  );

  const kbRetrieveAndGenerate = useCallback(
    async (query: string, sessionId?: string): Promise<KbGenerateResult> => {
      const response = await api.knowledgeBase.retrieveAndGenerate({
        query,
        ...(sessionId ? { sessionId } : {}),
      });
      return {
        answer: response.answer ?? '',
        citations: KnowledgeBaseApi.parseCitations(response.citations),
        ...(response.sessionId ? { sessionId: response.sessionId } : {}),
      };
    },
    [api],
  );

  return {
    snapshot,
    refs,
    refById,
    currentAlias: snapshot?.currentAlias ?? '',
    loading,
    error,
    refresh,
    converse,
    kbRetrieve,
    kbRetrieveAndGenerate,
  };
}
